import { type ReactNode, useState } from 'react';

import max from 'lodash/max';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import type { StaticImageData } from 'Components/Image';
import InfiniteCarousel from 'Components/InfiniteCarousel';
import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import SampleFormulationCard from './SampleFormulationCard';

const Container = styled.div`
  position: relative;
  width: 100%;
  background-color: ${theme.colors.primary['300']};
  padding: 50px 0;
  height: 1120px;
  ${legacyTheme.breakpoints.up('md')} {
    padding: 112px 0;
  }
`;

const SubTitle = styled(Typography)`
  font-size: 14px;
  padding: 0 24px;
`;

const Title = styled(Typography)`
  font-size: 28px;
  padding: 0 50px;
`;

const CardContainer = styled.div<{ selected: boolean }>`
  padding: ${({ selected }) => (selected ? '0px 12px 0px 30px' : '0px 12px')};
  ${legacyTheme.breakpoints.up('md')} {
    padding: 0 25px;
  }
`;

const CarouselContainer = styled.div`
  height: 100%;
`;

export type ImageType = {
  src: string | StaticImageData;
  alt: string;
};

export type Ingredient = {
  label: string;
  description: string;
  image: string | StaticImageData;
};

type CardType = {
  title: string;
  description: ReactNode;
  image: ImageType;
  concerns: string;
  environment: string;
  ingredients: Ingredient[];
};

type SampleFormulationCardCarouselProps = {
  subTitle: string;
  title: string;
  cards: CardType[];
};

export const SampleFormulationCardCarousel = ({
  cards,
  subTitle,
  title,
}: SampleFormulationCardCarouselProps) => {
  const [cardsHeight, setCardsHeight] = useState<Record<number, number | undefined>>({});
  const slideRender = (
    index: number,
    virtualIndex: number,
    selectedIndex: number,
    onClick: () => void,
    key: string,
  ) => {
    const card = cards[virtualIndex];
    if (!card) {
      return null; // Safely handle undefined case
    }

    return (
      <CardContainer key={key} selected={index === selectedIndex}>
        <SampleFormulationCard
          concerns={card.concerns}
          description={card.description}
          environment={card.environment}
          height={max(Object.values(cardsHeight))}
          image={card.image}
          ingredients={card.ingredients}
          onClick={onClick}
          opacity={index === selectedIndex ? 1 : 0.2}
          selected={index === selectedIndex}
          setHeight={(height) => setCardsHeight((state) => ({ ...state, [virtualIndex]: height }))}
          title={card.title}
        />
      </CardContainer>
    );
  };
  return (
    <Container>
      <SubTitle align="center" color="white" markupName="p" variant="mono1">
        {subTitle}
      </SubTitle>
      <Spacer size={16} />
      <Title align="center" color="white" markupName="h2" variant="h1">
        {title}
      </Title>
      <CarouselContainer>
        <InfiniteCarousel
          cardsLength={cards.length}
          dataClick="swipe-next"
          dataFrom="sample-formula-card"
          slideRender={slideRender}
        />
      </CarouselContainer>
    </Container>
  );
};
